import { useState } from "react"
import { useQuery as reactUseQuery } from "@tanstack/react-query"
import { FormInputText } from "../input/form-input-text"
import { FormModal, LoadingErrorModal } from "./form-modal"
import { FromDropdown } from "../input/form-dropdown"
import { useSelector } from "react-redux"
import { RootState } from "../../../store/configure-store"
import { newHttpService } from "../../../services/new-http-service"

export interface AuditYearDetails {
    year: number
    auditorId: number | null
}

interface AddAuditYearModalProps {
    entityName: string
    existingYears?: number[]
    onAddAuditYear: (auditYearDetails: AuditYearDetails) => void
    onCloseModal: () => void
    triggerMissingRequiredFields: () => void
    triggerErrorMsg: (txt: string) => void
}

export const AddAuditYearModal: React.FC<AddAuditYearModalProps> = (
    { entityName, existingYears = [], onAddAuditYear, onCloseModal, triggerMissingRequiredFields, triggerErrorMsg }
) => {
    const companyName: any = useSelector((stateModule: RootState) => stateModule.companyName)!
    const [auditors, setAuditors] = useState<{ id: number, name: string }[]>([])
    const [auditYearDetails, setAuditYearDetails] = useState<{ year: number, auditorId: number | null }>({ year: (new Date()).getFullYear(), auditorId: null })
    const [isShowSpinner, setIsShowSpinner] = useState(false)

    const fetchModalDetails = async () => {
        const query = `{
            entities(name:"${entityName}"){
                id
                name
                audit(limit: 1, sorting: DESC) {
                    id
                    year
                    auditor {
                        id
                    }
                }
            }
            auditors{
                id
                name
            }
        }
        `
        
        const res = await newHttpService.graphql(companyName.value, query)
        
        if (res.entities[0].audit.length) {
            const auditorId = res.entities[0].audit[0].auditor.id
            setAuditYearDetails(prevState => ({ ...prevState, auditorId }))
        }
        setAuditors(res.auditors)
        return res
    }

    const { isLoading, isRefetching, isError } = reactUseQuery({
        queryKey: ['modal-details'],
        queryFn: fetchModalDetails
    })

    const onChangeFormValue = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        const { name, value } = event.target
        let updatedValue: null | string | number = value
        if (value === '') updatedValue = null
        else if (['year', 'auditorId'].includes(name)) updatedValue = +value
        setAuditYearDetails({ ...auditYearDetails, [name]: updatedValue })
    }

    const onAdd = async () => {
        if (!auditYearDetails.year || !auditYearDetails.auditorId) {
            return triggerMissingRequiredFields()
        }

        if (auditYearDetails.year < 2024) {
            return triggerErrorMsg('Year must be greater than 2024')
        }
        if (existingYears.includes(auditYearDetails.year)) {
            return triggerErrorMsg(`Audit for year ${auditYearDetails.year} already exists for this entity`)
        }

        setIsShowSpinner(true)
        try {
            await onAddAuditYear(auditYearDetails)
        } catch {
            triggerErrorMsg('Failed creating audit year')
        } finally {
            setIsShowSpinner(false)
        }
    }

    const modalHeader = "Create Audit Year"

    if (isError) return <LoadingErrorModal
        header={modalHeader}
        onCloseModal={onCloseModal}
        isError={isError}
    />

    if (isLoading || isRefetching) return <LoadingErrorModal
        header={modalHeader}
        onCloseModal={onCloseModal}
    />

    return <FormModal
        header={modalHeader}
        onCloseModal={onCloseModal}
        actionBtnText="Create"
        onActionBtn={onAdd}
        isShowSpinner={isShowSpinner}
    >
        <div className="form-section-card">
            <div className="field">
                <FormInputText
                    label="Entity"
                    keyName=""
                    value={entityName}
                    onChange={onChangeFormValue}
                    isDisabled={true}
                />
            </div>
            <div className="field">
                <FormInputText
                    label="Year"
                    keyName="year"
                    isRequired={true}
                    value={auditYearDetails.year || ''}
                    onChange={onChangeFormValue}
                    isNumberInput={true}
                />
            </div>
            <div className="field">
                <FromDropdown
                    label="Auditor"
                    keyName="auditorId"
                    options={auditors}
                    value={auditYearDetails.auditorId || ''}
                    onSelectValue={onChangeFormValue}
                />
            </div>
        </div>
    </FormModal>
}